// builder-ux-overhaul Phase 4 Session 2 (2026-05-22) — 3-state active-version pill.
//
// Consumes `useActiveVersionDrift(conversationId)` (Phase-4 Session-1 hook)
// and renders one of three states keyed off `{ hasVersion, inSync }`:
//   ◌  no-version  — muted; deal has no active version yet
//   ▸  in-sync     — NEUTRAL (mirrors <CurrentVersionIndicator>, NOT accent per ADR 0019)
//   ▲  drifted     — warn; local edits diverge from the active version
//
// Folds into the Phase-3 <DealHeaderStrip> `pillSlot` (ADR 0006 — single prop
// pass; no DealHeaderStrip edit). Mounted on ALL THREE tabs via the
// route-level `pillSlot` pass-through in `routes/builder.tsx` + the wrappers
// `ModelHeaderStrip` + `SignalNewDevelopment` + `SignalOperatingAsset`.
//
// Tailwind v4 no-safelist (F03): pill state classes MUST be a STATIC literal
// `Record<PillState, string>` — NO `bg-${state}` interpolation.
//
// ADR 0019 color discipline:
//   in-sync = NEUTRAL (`text-text border-panel-border bg-panel-bg`)
//             mirrors `<CurrentVersionIndicator>` — MUST NOT be accent so it
//             never competes with `<AcceptedUnderwritingBadge>`.
//   drifted = warn family (`text-warn border-warn/30 bg-warn-dim`)
//   no-version = muted (`text-text-muted border-panel-border bg-panel-bg`)
//
// S2/S3 boundary: S2 renders the drifted-state action buttons but the restore
// onClick is an optional `onRestoreClick?: () => void` prop (undefined-safe).
// When undefined the button is present but inert. S3 supplies the real handler.

import clsx from 'clsx';
import { useActiveVersionDrift } from '@/hooks/use-active-version-drift';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PillState = 'no-version' | 'in-sync' | 'drifted';

// ---------------------------------------------------------------------------
// Static class lookups — Tailwind v4 no-safelist (F03).
// NEVER interpolate these values — they MUST be complete literal strings so
// Tailwind's static-scan extractor includes them in the build output.
// Color discipline: in-sync NEUTRAL, drifted warn, no-version muted (ADR 0019).
// ---------------------------------------------------------------------------

const PILL_WRAPPER_CLASS: Record<PillState, string> = {
  'no-version': 'text-text-muted border-panel-border bg-panel-bg',
  'in-sync': 'text-text border-panel-border bg-panel-bg',
  drifted: 'text-warn border-warn/30 bg-warn-dim',
};

const PILL_GLYPH: Record<PillState, string> = {
  'no-version': '◌',
  'in-sync': '▸',
  drifted: '▲',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActiveVersionPillProps {
  /** Active conversation id; the pill renders nothing when null/undefined. */
  conversationId: string | null | undefined;
  /**
   * S3 wires this prop. When undefined (S2 state), the "Restore to active"
   * button is present but click is a no-op (undefined-safe optional callback).
   */
  onRestoreClick?: () => void;
  /**
   * Mirror of `onRestoreClick`. Route owns the actual `useCreateVersion`
   * mutation (so the readiness gate + pending state stay co-located with
   * `<ManageVersionsButton>`'s instance). When undefined, the "Create new
   * version" button is rendered but inert — matches the safe-stub pattern
   * `onRestoreClick` set in S2.
   *
   * **Bug-history note (2026-05-25)**: the original implementation
   * dispatched `arcanum:open-versions` with no detail payload — the
   * `<BrowseVersionsModal>` listener guards on `detail.conversationId` and
   * silently rejected the event, so the button looked clickable but did
   * nothing. Even if it had opened, `<BrowseVersionsModal>` has no create
   * affordance (browse + per-row restore + diff only). Wiring the mutation
   * directly via this prop is the single durable fix.
   */
  onCreateClick?: () => void;
  /** When true, the Create button shows "Creating…" + disables. */
  createPending?: boolean;
  /** When true, the Create button is disabled (failed completeness gate). */
  createDisabled?: boolean;
  /** Disabled-tooltip copy. */
  createBlockedReason?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ActiveVersionPill({
  conversationId,
  onRestoreClick,
  onCreateClick,
  createPending = false,
  createDisabled = false,
  createBlockedReason = '',
}: ActiveVersionPillProps) {
  const { data, error } = useActiveVersionDrift(conversationId);

  // 404 (access / cross-user error) → render nothing; the route owns the 404 UX.
  // Also covers the loading state (data is undefined while fetching).
  // Phase-2 carry-forward #2: do NOT conflate 404 with no-version.
  if (error || !data) return null;

  const state: PillState = !data.hasVersion
    ? 'no-version'
    : data.inSync
    ? 'in-sync'
    : 'drifted';

  const glyph = PILL_GLYPH[state];

  return (
    <span
      data-testid="active-version-pill"
      data-state={state}
      className={clsx(
        // Base pill shape — mirrors <CurrentVersionIndicator>:
        // inline-flex, border, 2px radius, mono text.
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] border',
        'font-mono text-[10px] font-semibold tracking-[0.5px] uppercase whitespace-nowrap',
        // State-keyed color — static Record lookup (F03; ADR 0019).
        PILL_WRAPPER_CLASS[state]
      )}
    >
      {/* Leading glyph: ◌ / ▸ / ▲ */}
      <span aria-hidden="true">{glyph}</span>

      {state === 'no-version' && <span>NO ACTIVE VERSION</span>}

      {state === 'in-sync' && (
        <span>ACTIVE · {data.currentVersionLabel ?? ''}</span>
      )}

      {state === 'drifted' && (
        <>
          <span>
            ACTIVE · {data.currentVersionLabel ?? ''} · UNSAVED CHANGES
          </span>

          {/* Drifted-state action affordances.
              S2 renders these; S3 wires onRestoreClick.
              Button is present when onRestoreClick is undefined but click is a no-op. */}
          <span
            className="flex items-center gap-1.5 ml-1.5 border-l border-warn/30 pl-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* "Restore to active" — warn-outline style */}
            <button
              type="button"
              data-testid="active-version-pill-restore-btn"
              onClick={onRestoreClick}
              className={clsx(
                'px-1.5 py-px rounded-[2px] border cursor-pointer transition-colors',
                'text-warn border-warn/40 bg-transparent hover:bg-warn/10',
                'text-[9px] font-mono font-semibold tracking-[0.4px] uppercase',
                !onRestoreClick && 'cursor-default'
              )}
            >
              Restore to active
            </button>

            {/* "Create new version" — accent guidance affordance.
                Wired via `onCreateClick` prop (route owns the mutation —
                shares `useCreateVersion` semantics with <ManageVersionsButton>).
                Bug-history: see the prop JSDoc above. */}
            <button
              type="button"
              data-testid="active-version-pill-create-btn"
              disabled={!onCreateClick || createDisabled || createPending}
              title={
                createDisabled && createBlockedReason
                  ? createBlockedReason
                  : 'Save the current model state as a new version.'
              }
              onClick={() => onCreateClick?.()}
              className={clsx(
                'px-1.5 py-px rounded-[2px] border transition-colors',
                'text-[9px] font-mono font-semibold tracking-[0.4px] uppercase',
                !onCreateClick || createDisabled || createPending
                  ? 'text-text-dim border-panel-border bg-transparent cursor-not-allowed opacity-60'
                  : 'text-accent border-accent/30 bg-accent/5 hover:bg-accent/10 cursor-pointer'
              )}
            >
              {createPending ? 'Creating…' : 'Create new version'}
            </button>
          </span>
        </>
      )}
    </span>
  );
}
